import { savePosition } from "../ReusableFunctions/playbackPosition";
import { useState } from "react";
import {
  RewindTen,
  ForwordTen,
  VolumeIcon,
  VolumeMuteIcon,
  DownloadIcon,
  PauseIcon,
  PlayIcon,
} from "../assets/icon-file";

export default function CustomAudioVideoControls({
  mediaRef,
  mediaUrl,
  modal,
  isPlaying,
  currentTime,
  setCurrentTime,
  duration,
  handlePlay,
  logRecentlyPlayed,
  handlePause,
  setIsSeeking,
  handleStartHereTime,
  addEmbedCodeToMediaData,
  setMediaData,
  mediaData,
  checked,
}) {
  const [speed, setSpeed] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false);

  const playPauseToggle = () => {
    if (mediaRef.current.paused) {
      // Guarantees video will restart from the beginning if it was at the end
      if (currentTime >= duration) {
        mediaRef.current.currentTime = 0; // Reset to start if at the end
        setCurrentTime(0);
      }
      // play() returns a promise that rejects if the request is interrupted
      // before playback starts — e.g. the HLS source attaching, or a seek while
      // still buffering, which is common on slow connections. That rejection is
      // benign; swallow it so it isn't an uncaught AbortError in the console.
      const playPromise = mediaRef.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
      if (modal) {
        handlePlay();
      } else if (localStorage.getItem("accessToken") !== null) {
        logRecentlyPlayed();
      }
    } else {
      if (modal) {
        handlePause();
      }
      mediaRef.current.pause();
    }
  };

  const handlePlaybackSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    mediaRef.current.playbackRate = newSpeed;
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    mediaRef.current.volume = newVolume;
    setIsMuted(false);
  };

  const toggleMute = () => {
    const newIsMuted = !isMuted;
    setIsMuted(newIsMuted);
    mediaRef.current.muted = newIsMuted;
  };

  const handleSeeking = (newTime) => {
    // Seeking state prevents onTimeUpdate from updating currentTime while dragging
    setIsSeeking(true);
    const updatedTime =
      newTime < 0 ? 0 : newTime > duration ? duration : newTime;
    mediaRef.current.currentTime = updatedTime;
    setCurrentTime(updatedTime);
    if (modal) {
      handleModalSeeking(updatedTime);
    } else {
      savePosition(mediaUrl, updatedTime);
    }
    setIsSeeking(false);
  };

  const handleModalSeeking = (updatedTime) => {
    handleStartHereTime(updatedTime);
    if (checked) {
      const mediaDataWithEmbedCode = addEmbedCodeToMediaData({
        mediaData: mediaData,
        startHere: updatedTime,
      });
      setMediaData(mediaDataWithEmbedCode);
    }
  };

  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    return `${
      hours
        ? `${String(hours)}:${String(minutes).padStart(2, "0")}`
        : `${String(minutes)}`
    }:${String(seconds).padStart(2, "0")}`;
  };

  const downloadMedia = (url) => {
    // Create a link element
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    //a.download = 'downloaded_audio.mp3'; // You can specify the filename here

    // Append the link to the body
    document.body.appendChild(a);

    // Trigger the download by simulating a click on the link
    a.click();

    // Clean up by removing the link and revoking the blob URL
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <>
      <button
        className={`${modal ? "hidden" : "hidden sm:inline text-2xl"}`}
        onClick={playPauseToggle}
      >
        {isPlaying ? (
          <PauseIcon dimensions="w-[41px] h-[41px]" />
        ) : (
          <PlayIcon dimensions="w-[41px] h-[41px]" />
        )}
      </button>
      <div
        className={`${
          modal ? "hidden" : "hidden sm:flex flex-col flex-1 relative bottom-1"
        }`}
      >
        <PlayInput
          modal={modal}
          duration={duration}
          currentTime={currentTime}
          handleSeeking={handleSeeking}
          formatTime={formatTime}
        />
      </div>
      <div
        className={`flex items-center ${
          modal ? "w-full" : "w-full sm:w-max sm:gap-5"
        } justify-between`}
      >
        <button
          className={`${modal ? "text-2xl" : "sm:hidden text-2xl"} `}
          onClick={playPauseToggle}
        >
          {isPlaying ? (
            <PauseIcon
              dimensions={
                modal ? "w-8 h-8 lg:w-[41px] lg:h-[41px]" : "w-[41px] h-[41px]"
              }
            />
          ) : (
            <PlayIcon
              dimensions={
                modal ? "w-8 h-8 lg:w-[41px] lg:h-[41px]" : "w-[41px] h-[41px]"
              }
            />
          )}
        </button>
        <div>
          <select
            className="bg-primaryDark"
            value={speed}
            onChange={(e) =>
              handlePlaybackSpeedChange(parseFloat(e.target.value))
            }
          >
            <option value={0.5}>0.5x</option>
            <option value={1.0}>1.0x</option>
            <option value={1.5}>1.5x</option>
            <option value={2.0}>2.0x</option>
          </select>
        </div>
        <div className="flex items-center gap-5">
          <button onClick={() => handleSeeking(currentTime - 10)}>
            <RewindTen />
          </button>
          <button onClick={() => handleSeeking(currentTime + 10)}>
            <ForwordTen />
          </button>
        </div>
        <div className="flex gap-1 items-center">
          <span onClick={toggleMute}>
            {isMuted ? <VolumeMuteIcon /> : <VolumeIcon />}
          </span>
          <input
            type="range"
            className="h-1 w-16"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
          />
        </div>
        <div className="flex items-center gap-5">
          <button onClick={() => downloadMedia(mediaUrl)}>
            <DownloadIcon />
          </button>
        </div>
      </div>
      <div
        className={`${
          modal
            ? "flex flex-col flex-1 relative w-full"
            : "flex sm:hidden flex-col flex-1 relative w-full"
        }`}
      >
        <PlayInput
          modal={modal}
          duration={duration}
          currentTime={currentTime}
          formatTime={formatTime}
          handleSeeking={handleSeeking}
        />
      </div>
    </>
  );
}

export const PlayInput = ({
  modal,
  duration,
  currentTime,
  formatTime,
  handleSeeking,
}) => {
  return (
    <>
      <input
        type="range"
        className="w-full cursor-pointer h-1"
        min="0"
        max={duration}
        step="0.00000000000001"
        onChange={(e) => handleSeeking(parseFloat(e.target.value))}
        value={currentTime}
      />
      <div
        className={`flex absolute top-3 ${
          modal ? "lg:top-6" : ""
        } w-full justify-between text-xs`}
      >
        <span>
          {/* Time won't exceed duration of media */}
          {formatTime(currentTime <= duration ? currentTime : duration)}
        </span>{" "}
        <span>{formatTime(duration)}</span>
      </div>
    </>
  );
};
